import { ChevronRight, ExpandMore, Lock, LockOpen } from "@mui/icons-material";
import { Box, IconButton, ListItemButton, Tooltip, Typography } from "@mui/material";
import { OctreeDebugNode } from "@novorender/api";

import { octreeLevelColor } from "./octreeNodeColor";

export function OctreeTreeNode({
    node,
    expanded,
    selectedId,
    noSplitIds,
    isolatedSubtreeIds,
    onToggle,
    onSelect,
    onToggleNoSplit,
}: {
    node: OctreeDebugNode;
    expanded: Set<string>;
    selectedId: string | undefined;
    noSplitIds: Set<string>;
    isolatedSubtreeIds: Set<string> | null;
    onToggle: (id: string) => void;
    onSelect: (node: OctreeDebugNode) => void;
    onToggleNoSplit: (id: string) => void;
}) {
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded.has(node.id);
    const isSelected = node.id === selectedId;
    const isNoSplit = noSplitIds.has(node.id);
    const isOutsideIsolation = isolatedSubtreeIds !== null && !isolatedSubtreeIds.has(node.id);

    return (
        <>
            <ListItemButton
                dense
                selected={isSelected}
                onClick={() => onSelect(node)}
                sx={{ pl: 1 + node.level * 1.5, py: 0.25, pr: 0.5, opacity: isOutsideIsolation ? 0.35 : 1 }}
            >
                <Box
                    width={20}
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    onClick={(e) => {
                        if (hasChildren) {
                            e.stopPropagation();
                            onToggle(node.id);
                        }
                    }}
                >
                    {hasChildren ? (
                        isExpanded ? (
                            <ExpandMore fontSize="small" />
                        ) : (
                            <ChevronRight fontSize="small" />
                        )
                    ) : null}
                </Box>
                <Box
                    width={12}
                    height={12}
                    flexShrink={0}
                    mr={1}
                    borderRadius="2px"
                    sx={{ backgroundColor: octreeLevelColor(node.level), border: "1px solid rgba(0,0,0,0.2)" }}
                />
                <Box overflow="hidden" flex={1}>
                    <Typography noWrap variant="body2" title={node.id}>
                        {node.id.slice(-6) || "root"} ({node.level})
                    </Typography>
                    <Typography noWrap variant="caption" color="textSecondary">
                        {node.state} · {node.visibility} · {node.children.length} child
                    </Typography>
                </Box>
                <Tooltip title={isNoSplit ? "Allow split" : "Lock (no split)"} placement="left">
                    <IconButton
                        size="small"
                        color={isNoSplit ? "warning" : "default"}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleNoSplit(node.id);
                        }}
                        sx={{ p: 0.25 }}
                    >
                        {isNoSplit ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </ListItemButton>
            {hasChildren &&
                isExpanded &&
                node.children.map((child) => (
                    <OctreeTreeNode
                        key={child.id}
                        node={child}
                        expanded={expanded}
                        selectedId={selectedId}
                        noSplitIds={noSplitIds}
                        isolatedSubtreeIds={isolatedSubtreeIds}
                        onToggle={onToggle}
                        onSelect={onSelect}
                        onToggleNoSplit={onToggleNoSplit}
                    />
                ))}
        </>
    );
}
